import React, { useEffect, useRef } from "react"
import flatpickr from "flatpickr"
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance"
import { focusNextField } from "helpers/formKeyboardNav"
import "flatpickr/dist/flatpickr.min.css"
import "flatpickr/dist/themes/material_blue.css"
import "./ModernDatePicker.scss"

export interface ModernDatePickerProps {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  enableTime?: boolean
  disabled?: boolean
  className?: string
  minDate?: string | Date
  maxDate?: string | Date
}

export function normalizeStoredDateTime(value: string | undefined): string {
  if (!value?.trim()) return ""
  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed.slice(0, 16))) {
    return trimmed.slice(0, 16)
  }
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return trimmed.slice(0, 16)
  const ist = d.toLocaleString("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
  return ist.replace(", ", "T")
}

export function storedDateTimeToPickerValue(stored: string | undefined): string {
  if (!stored?.trim()) return ""
  return normalizeStoredDateTime(stored).replace("T", " ")
}

export function pickerValueToStoredDateTime(pickerValue: string): string {
  if (!pickerValue?.trim()) return ""
  return pickerValue.trim().replace(" ", "T").slice(0, 16)
}

const ModernDatePicker: React.FC<ModernDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  enableTime = false,
  disabled,
  className = "",
  minDate,
  maxDate,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const fpRef = useRef<FlatpickrInstance | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const pickerValue = enableTime
    ? storedDateTimeToPickerValue(value)
    : (value?.trim().slice(0, 10) || "")

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    fpRef.current = flatpickr(input, {
      altInput: true,
      altFormat: enableTime ? "j M Y, h:i K" : "j M Y",
      altInputClass: "form-control modern-date-picker-display",
      dateFormat: enableTime ? "Y-m-d H:i" : "Y-m-d",
      enableTime,
      time_24hr: false,
      minuteIncrement: 1,
      disableMobile: true,
      clickOpens: true,
      allowInput: false,
      appendTo: document.body,
      minDate,
      maxDate,
      onChange: (_dates, dateStr) => {
        onChangeRef.current(
          enableTime ? pickerValueToStoredDateTime(dateStr) : (dateStr || "")
        )
      },
    })

    const fp = fpRef.current
    const focusTarget = fp.altInput ?? fp.input
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
      event.preventDefault()
      fp.close()
      focusNextField(event.target as HTMLElement)
    }
    focusTarget.addEventListener("keydown", onKeyDown)

    return () => {
      focusTarget.removeEventListener("keydown", onKeyDown)
      fp.destroy()
      fpRef.current = null
    }
  }, [enableTime, minDate, maxDate])

  useEffect(() => {
    const fp = fpRef.current
    if (!fp) return
    if (pickerValue) {
      fp.setDate(pickerValue, false)
    } else {
      fp.clear()
    }
  }, [pickerValue])

  useEffect(() => {
    const fp = fpRef.current
    if (!fp) return
    if (disabled) fp.input.setAttribute("disabled", "disabled")
    else fp.input.removeAttribute("disabled")
    if (fp.altInput) {
      if (disabled) fp.altInput.setAttribute("disabled", "disabled")
      else fp.altInput.removeAttribute("disabled")
    }
  }, [disabled])

  return (
    <div className={`modern-date-picker-wrap ${className}`.trim()}>
      <input
        ref={inputRef}
        type="text"
        className="form-control d-block modern-date-picker"
        placeholder={placeholder || (enableTime ? "Select date & time" : "Select date")}
        disabled={disabled}
        readOnly
      />
    </div>
  )
}

export default ModernDatePicker
